import React, { useState } from 'react';
import { X, Key, Mail } from 'lucide-react';
import { adminService } from '../../services/adminService.js';
import { toast } from 'react-hot-toast';

const PasswordResetModal = ({
  isOpen,
  onClose,
  user
}) => {
  const [isSending, setIsSending] = useState(false);

  if (!isOpen || !user) return null;

  const handleSendReset = async () => {
    setIsSending(true);

    try {
      const result = await adminService.sendUserPasswordReset(user.email);

      if (result.success) {
        toast.success('Password reset email sent successfully!');
        onClose();
      } else {
        toast.error(result.error || 'Failed to send password reset email');
      }
    } catch (error) {
      console.error('Error sending password reset:', error);
      toast.error(error.message || 'Failed to send password reset email');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Key className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Send Password Reset</h2>
              <p className="text-sm text-gray-500">Reset link will be sent to user's email</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Mail className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-900">
                  Password reset email will be sent to:
                </p>
                <p className="mt-1 text-sm text-blue-700 font-semibold">
                  {user.email}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              The user will receive an email with a link to reset their password. The link will expire in 1 hour.
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-xs text-yellow-800">
              <strong>Note:</strong> Make sure the user has access to this email address before sending the reset link.
            </p>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors"
            disabled={isSending}
          >
            Cancel
          </button>
          <button
            onClick={handleSendReset}
            disabled={isSending}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {isSending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Mail className="w-4 h-4" />
                <span>Send Reset Email</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetModal;

